import { useHistory, useLocation } from "react-router-dom";
import { BulkClassQuery } from "src/api/Class";
import { FeesQuery } from "src/api/Fee";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import { AddItemHeader } from "src/components/forms/AddItemHeader";
import Container from "src/components/layout/Container";
import { BulkEditCheckoutModal } from "src/components/modals/BulkEditCheckoutModal";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import formatMoney from "src/utils/formatMoney";
import { modals } from "src/utils/modals";
import { useSearchParams } from "src/utils/useSearchParams";
import {
  AmountType,
  bulkClassVariables,
  fees,
  feesVariables,
} from "types/code-generator";

interface Props {}

export default function BulkEditFees(props: Props) {
  const history = useHistory();
  const location = useLocation();
  const store = useStore();
  const params = useSearchParams();

  const ids = params.getAll("id");
  const excluded = params.has("exclude");

  const feesVariables: feesVariables = {
    where: {
      organizationId: store.organization.id,
      departmentId: undefined,
    },
  };

  const queryFees = useApi(FeesQuery, {
    variables: feesVariables,
  });

  const variables: bulkClassVariables = {
    filter: location.search,
    where: {
      organizationId: store.organization.id,
      id:
        ids.length > 0
          ? excluded
            ? { notIn: ids }
            : {
                in: ids,
              }
          : undefined,
    },
  };

  const bulkEditQuery = useApi(BulkClassQuery, {
    variables,
  });

  const counts = bulkEditQuery.bulkClass;
  const fees = queryFees.fees || [];

  function onCancel() {
    history.goBack();
  }

  function onNetworkAction() {
    bulkEditQuery.refetch();
  }

  function bulkEditFee(fee: fees["fees"][0]) {
    modals.show(BulkEditCheckoutModal, {
      field: "fees",
      counts,
      itemId: fee.id,
      itemTitle: fee.title,
      onNetworkAction,
    });
  }

  // console.log("offering", offering);
  return (
    <>
      <AddItemHeader>
        <Container large>
          <BackButton backText="Return to Bulk Edit" />
          <h2 className="mb-0 flex-1">Line Item Fees</h2>
        </Container>
      </AddItemHeader>

      <Container large className="bg-white pb-24 pt-24">
        <Container fullSmall>
          <p className="mb-4 pt-4">
            Select a line item fee to enable or disable for all selected
            offerings.
          </p>

          <hr className="mb-0" />

          <div className="divide-y divide-borderGray">
            {fees
              .filter((a) => !a.availableForAllOfferings)
              .map((a) => {
                const count = counts?.fees[a.id]?.length || 0;
                const amount =
                  a.amountType === AmountType.Dollar
                    ? `$${formatMoney(a.amount)}`
                    : `${a.amount.toFixed(2)}%`;

                let enabled = `Enabled on ${count.toLocaleString()} of ${counts?.total.toLocaleString()} selected offering${
                  count !== 1 ? "s" : ""
                }.`;

                if (count === counts?.total) {
                  enabled = `Enabled on all ${counts?.total.toLocaleString()} selected offering${
                    counts?.total !== 1 && "s"
                  }.`;
                }

                return (
                  <button
                    key={a.id}
                    className="flex w-full items-center px-4 py-4 text-left"
                    type="button"
                    onClick={() => bulkEditFee(a)}
                  >
                    <div className="flex-1 pr-8">
                      <strong className="text-dark">{a.title}</strong>
                      <div>{amount}</div>

                      <div className="mb-2 text-sm text-empty">{enabled}</div>
                    </div>
                    <i className="far fa-chevron-right text-organization" />
                  </button>
                );
              })}

            <hr className="my-0" />
          </div>

          <div className="flex pt-4">
            <i className="far fa-triangle-exclamation mr-2 mt-1 text-main" />
            <div className="flex-1">
              Line item fees apply to all one-time and recurring charges
              associated with this offering.
            </div>
          </div>

          <div className="pb-12 pt-8">
            <BorderButton onClick={onCancel}>Return to Bulk Edit</BorderButton>
          </div>
        </Container>
      </Container>
    </>
  );
}
